import { Entity, generateEntityId } from './entity';
import { WA_PHYSICS, type Vector2 } from '../core/constants';

export type CrateType = 'weapon' | 'health' | 'utility';

export class Crate implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2 = { x: 0, y: 0 };
  active = true;

  type: CrateType;
  contents: string;
  parachuteOpen = false;
  isGrounded = false;

  explosionRadius = 30;
  maxDamage = 25;

  onCollect?: (wormId: string) => void;
  onExplode?: (x: number, y: number) => void;

  /** Without a parachute, crates drop under gravity instead of drifting. */
  private static readonly FALL_SPEED = 40;
  private static readonly WIDTH = 16;
  private static readonly HEIGHT = 16;

  constructor(x: number, y: number, type: CrateType, contents: string) {
    this.id = generateEntityId('crate');
    this.position = { x, y };
    this.type = type;
    this.contents = contents;
  }

  update(deltaTime: number): void {
    if (!this.active) return;

    if (this.parachuteOpen) {
      this.velocity.y = Crate.FALL_SPEED;
    } else if (!this.isGrounded) {
      this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
    }

    if (!this.isGrounded) {
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
    }

    if (this.isGrounded) {
      this.velocity.x *= 0.9;
      if (Math.abs(this.velocity.x) < 1) {
        this.velocity.x = 0;
      }
    }
  }

  applyTerrainPhysics(terrain: { isSolid(x: number, y: number): boolean }): void {
    if (!this.active) return;

    const halfW = Crate.WIDTH / 2;
    const halfH = Crate.HEIGHT / 2;

    if (this.velocity.x !== 0) {
      const sideX = this.velocity.x > 0 ? this.position.x + halfW : this.position.x - halfW;
      const hitsSide =
        terrain.isSolid(sideX, this.position.y - halfH + 2) ||
        terrain.isSolid(sideX, this.position.y) ||
        terrain.isSolid(sideX, this.position.y + halfH - 2);
      if (hitsSide) {
        while (this.velocity.x > 0 && terrain.isSolid(this.position.x + halfW, this.position.y)) {
          this.position.x -= 1;
        }
        while (this.velocity.x < 0 && terrain.isSolid(this.position.x - halfW, this.position.y)) {
          this.position.x += 1;
        }
        this.velocity.x = -this.velocity.x * 0.3;
      }
    }

    const feetY = this.position.y + halfH;

    if (this.velocity.y >= 0) {
      const groundY = this.findGroundBelow(this.position.x, feetY, terrain);
      if (groundY !== null && groundY - feetY <= 2) {
        this.position.y = groundY - halfH;
        this.velocity.y = 0;
        this.parachuteOpen = false;
        this.isGrounded = true;
        return;
      }
    }

    const hasGroundBelow =
      terrain.isSolid(this.position.x - halfW + 2, feetY + 1) ||
      terrain.isSolid(this.position.x, feetY + 1) ||
      terrain.isSolid(this.position.x + halfW - 2, feetY + 1);
    if (!hasGroundBelow) {
      this.isGrounded = false;
    }
  }

  private findGroundBelow(
    x: number,
    startY: number,
    terrain: { isSolid(x: number, y: number): boolean },
  ): number | null {
    for (let dy = 0; dy <= 5; dy++) {
      if (terrain.isSolid(x, startY + dy)) {
        return startY + dy;
      }
    }
    return null;
  }

  checkWormCollision(
    worms: {
      id: string;
      position: Vector2;
      state: string;
      getBounds(): { x: number; y: number; width: number; height: number };
    }[],
  ): string | null {
    if (!this.active || !this.isGrounded) return null;

    const crateBox = this.getBounds();

    for (const worm of worms) {
      if (worm.state === 'dead') continue;

      const wormBox = worm.getBounds();
      const overlapX =
        Math.min(crateBox.x + crateBox.width, wormBox.x + wormBox.width) -
        Math.max(crateBox.x, wormBox.x);
      const overlapY =
        Math.min(crateBox.y + crateBox.height, wormBox.y + wormBox.height) -
        Math.max(crateBox.y, wormBox.y);

      if (overlapX > 0 && overlapY > 0) {
        return worm.id;
      }
    }

    return null;
  }

  collect(wormId: string): void {
    if (!this.active) return;
    this.active = false;
    this.onCollect?.(wormId);
  }

  explode(): void {
    if (!this.active) return;
    this.active = false;
    this.onExplode?.(this.position.x, this.position.y);
  }

  applyExplosionForce(pushX: number, pushY: number): void {
    this.velocity.x += pushX * 0.5;
    this.velocity.y += pushY * 0.5;
    this.parachuteOpen = false;
    this.isGrounded = false;
  }

  takeDamage(_amount: number): void {
    this.explode();
  }

  getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.position.x - Crate.WIDTH / 2,
      y: this.position.y - Crate.HEIGHT / 2,
      width: Crate.WIDTH,
      height: Crate.HEIGHT,
    };
  }

  destroy(): void {
    this.active = false;
  }
}
